<script lang="ts" setup>
import { EcommerceProduct } from '@zyro-inc/site-modules/types';
import ProductMediaElement from '@zyro-inc/site-modules/components/ecommerce/ProductMediaElement.vue';
import { formatPrice } from '@zyro-inc/site-modules/utils/ecommerce/priceFormatter';
import ArrowButton from '@zyro-inc/site-modules/components/ecommerce/-partials/ArrowButton.vue';
import { useArrowNavigation } from '@zyro-inc/site-modules/use/useArrowNavigation';
import { ref } from 'vue';

import {
	getProductImage,
	getProductPrice,
} from '@zyro-inc/site-modules/utils/ecommerce/productUtils';

const props = defineProps<{
	products: EcommerceProduct[];
	translations: Record<string, string>;
	siteId: string;
}>();

const emits = defineEmits<{
	'add-to-bag': [EcommerceProduct];
}>();

const productsContainer = ref<HTMLElement | null>(null);
const {
	handleArrowClick,
	isLeftDisabled,
	isRightDisabled,
} = useArrowNavigation({
	productsContainer,
	productsLength: props.products.length,
});

const handleProductClick = (product: EcommerceProduct) => {
	window.location.assign(`/${product.seo_settings.slug}`);
};
</script>

<template>
	<div class="ecommerce-product-suggestions">
		<div class="ecommerce-product-suggestions__header">
			<p class="ecommerce-product-suggestions__title">
				{{ translations.youMayAlsoLike }}
			</p>
			<div class="ecommerce-product-suggestions__arrows">
				<ArrowButton
					direction="left"
					:disabled="isLeftDisabled"
					@click="handleArrowClick({ direction: 'left' })"
				/>
				<ArrowButton
					direction="right"
					:disabled="isRightDisabled"
					@click="handleArrowClick({ direction: 'right' })"
				/>
			</div>
		</div>

		<ul
			ref="productsContainer"
			class="ecommerce-product-suggestions__products"
		>
			<li
				v-for="product in props.products"
				:key="product.id"
				class="ecommerce-product-suggestions__product"
				@click="handleProductClick(product)"
			>
				<ProductMediaElement
					v-if="product.thumbnail"
					:src="getProductImage({ product })"
					:alt="product.title"
					class="ecommerce-product-suggestions__product-image"
					:width="64"
					:height="64"
					:site-id="siteId"
					enable-srcset
				/>
				<div class="ecommerce-product-suggestions__product-info">
					<div class="ecommerce-product-suggestions__product-info-content">
						<p
							class="ecommerce-product-suggestions__product-title"
							:title="product.title"
						>
							{{ product.title }}
						</p>
						<div class="ecommerce-product-suggestions__product-price-container">
							<p
								class="ecommerce-product-suggestions__price"
								:class="{ 'ecommerce-product-suggestions__old-price' : product.variants[0].prices[0].sale_amount }"
							>
								{{ getProductPrice({
									product: product,
									freeProductTranslation: translations.free,
								}) }}
							</p>

							<p
								v-if="product.variants[0].prices[0].sale_amount"
								class="ecommerce-product-suggestions__price"
							>
								{{ formatPrice({
									amount: product.variants[0].prices[0].sale_amount,
									currency: product.variants[0].prices[0].currency
								}) }}
							</p>
						</div>
					</div>

					<button
						class="ecommerce-product-suggestions__add-to-bag"
						@click.stop="emits('add-to-bag', product)"
					>
						{{ translations.addToBag }}
					</button>
				</div>
			</li>
		</ul>
	</div>
</template>

<style lang="scss" scoped>
.ecommerce-product-suggestions {
	display: flex;
	flex-direction: column;
    background-color: $color-neutral-50;
    margin: 32px -24px;
    padding:  8px 24px 16px;
    width: calc(100% + 48px);
    min-height: 215px;
    overflow: hidden;

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 24px;
    }

    &__title {
        font-size: 16px;
        font-weight: 500;
        line-height: 24px;
    }

    &__arrows {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    &__products {
        display: flex;
        gap: 32px;
        overflow-x: auto;
        margin: 0 -24px 0 0;
        width: calc(100% + 24px);
        padding: 0  100px 0 0;
        list-style: none;
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE and Edge */

        &::-webkit-scrollbar {
            display: none; /* Chrome, Safari and Opera */
        }
    }

    &__product {
        display: flex;
        align-items: start;
        gap: 16px;
        width: 275px;
        min-width: 275px;
        cursor: pointer;
    }

    &__product-image {
        width: 64px;
        height: 64px;
        border-radius: 8px;
        border: 1px solid $color-gray-border;
    }

	&__product-info-content {
		min-height: 60px;
	}

    &__product-info {
        display: flex;
        flex-direction: column;
        align-items: start;
        height: 100%;
    }

    &__product-title {
        font-size: 16px;
		margin-bottom: 4px;
        line-height: 1.2;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        max-height: 72px; /* 3 lines * 24px line height */
    }

    &__product-price-container {
        display: flex;
        gap: 4px;
    }

    &__price {
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;

        &--strikethrough {
            text-decoration: line-through;
        }
    }

    &__old-price {
        text-decoration: line-through
    }

    &__add-to-bag {
        margin-top: 8px;
        margin-bottom: auto;
        padding: 8px 16px;
        border-radius: 8px;
        border: 1px solid $color-dark;
        background-color: $color-light;
        color: $color-dark;
        font-size: 14px;
        font-weight: 400;
        cursor: pointer;
        transition:
            color var(--grid-button-primary-transition-duration) var(--grid-button-primary-transition-timing-function),
            border-color var(--grid-button-primary-transition-duration) var(--grid-button-primary-transition-timing-function),
            background-color var(--grid-button-primary-transition-duration) var(--grid-button-primary-transition-timing-function);

        &:hover {
            background-color: $color-dark;
            color: $color-light;
        }
    }
}

</style>
